import type {ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {AiChatTestId} from '@/test-ids/ai-chat-test-id';
import {truncateMiddle} from '@Utils/truncate-middle.util';
import type {DocumentChipProps} from './document-chip.types';
import {FILENAME_MAX_CHARS, ICON_CLASS_BY_TYPE, STEP_LABEL_KEY} from './document-chip.types';
import {getChipAccessibleName} from './document-chip.library';
import {DocumentChipStepText} from './components/document-chip-step-text/document-chip-step-text';
import styles from './document-chip.module.scss';

export function DocumentChip({filename, documentType, onClick, isDisabled = false, step, failure}: DocumentChipProps): ReactElement {
  const typeLabel = documentType.toUpperCase();
  const isFailed = failure !== undefined;
  const stepLabel = step === undefined ? undefined : window.i18next.t(STEP_LABEL_KEY[step]);
  const accessibleName = getChipAccessibleName(filename, typeLabel, failure?.message ?? stepLabel);

  const getHeaderIcon = (): ReactElement => {
    if (step !== undefined) {
      return <CircularProgressLoader color={LOADER_COLOR.DARK} size={LOADER_SIZE.XXSMALL} classes="_unmargin" />;
    }
    return <Icon icon={isFailed ? 'icon-exclamation-circle' : ICON_CLASS_BY_TYPE[documentType]} size={IconSize.SIZE_ICON_12} className={styles.icon} ariaHidden />;
  };

  const getHeaderText = (): ReactElement => {
    if (isFailed || stepLabel === undefined) {
      return <Text val={failure?.summary ?? typeLabel} size={TextSize.SM} bold className={isFailed ? styles.failureSummary : undefined} />;
    }
    return <DocumentChipStepText key={step} stepLabel={stepLabel} />;
  };

  return (
    <button
      type="button"
      className={`${styles.chip} ${isDisabled ? '' : styles.interactive} ${isFailed ? styles.failed : ''}`}
      onClick={isDisabled ? undefined : onClick}
      title={isFailed ? `${filename}: ${failure.message}` : filename}
      aria-label={accessibleName}
      aria-disabled={isDisabled}
      data-testid={AiChatTestId.DOCUMENT_CHIP}
    >
      <span className={styles.header}>
        {getHeaderIcon()}
        {getHeaderText()}
      </span>
      <Text val={truncateMiddle(filename, FILENAME_MAX_CHARS)} size={TextSize.SM} className={styles.filename} />
    </button>
  );
}
